// app/frontend/components/ui/NavigationDrawer.tsx
import { Drawer, Text } from "@mantine/core";
import React from "react";

export interface NavigationDrawerProps {
  /**
   * Whether the drawer is open
   */
  opened: boolean;
  /**
   * Callback function when drawer should close
   */
  onClose: () => void;
  /**
   * Optional drawer title (defaults to "Menu")
   */
  title?: string;
  /**
   * Optional drawer size (defaults to "xs")
   */
  size?: string | number;
  /**
   * Custom content/children for the drawer
   */
  children: React.ReactNode;
}

/**
 * NavigationDrawer - Reusable navigation drawer component
 *
 * Features:
 * - Consistent styling and spacing across layouts
 * - Customizable title and size
 * - Accepts custom children for flexible content
 * - External state management for open/close control
 *
 * @example
 * <NavigationDrawer
 *   opened={menuOpen}
 *   onClose={() => setMenuOpen(false)}
 *   title="Menu"
 * >
 *   <Stack gap="lg">
 *     <MenuNavItem href="/videos" icon={IconVideo} label="Videos" />
 *   </Stack>
 * </NavigationDrawer>
 */
export function NavigationDrawer({
  opened,
  onClose,
  title = "Menu",
  size = "xs",
  children,
}: NavigationDrawerProps) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg" style={{ letterSpacing: 0.3 }}>
          {title}
        </Text>
      }
      padding="md"
      size={size}
      styles={{
        content: {
          background: "rgba(23, 23, 23, 0.95)",
          borderLeft: "1px solid rgba(255, 152, 0, 0.3)",
          backdropFilter: "blur(15px)",
        },
        header: {
          backgroundColor: "transparent",
          borderBottom: "1px solid rgba(255, 152, 0, 0.3)",
          paddingBottom: "16px",
        },
        title: { color: "#F0F0F0" },
      }}
    >
      {children}
    </Drawer>
  );
}

